//AWS and REACT Libraries 

import { Authenticator, useAuthenticator } from '@aws-amplify/ui-react';
import React, { useEffect, useState } from 'react';
import { useGetNotificationsQuery } from '../store/api/notificationApi';

//page components
import MessagesData from '../section/MessagesData.js'//used to show the user when they are logged in how to use the website
import PortalPageLayout from '../components/PortalPageLayout.js';

//boostrap components 
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

const parseMessageTimestamp = (timestamp) => {
  const match = `${timestamp || ''}`.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})\s+(\d{1,2}):(\d{2})\s+(AM|PM)$/i);
  if (!match) {
    return 0;
  }

  const month = Number(match[1]) - 1;
  const day = Number(match[2]);
  const rawYear = Number(match[3]);
  const year = match[3].length === 2 ? 2000 + rawYear : rawYear;
  let hours = Number(match[4]);
  const minutes = Number(match[5]);
  const meridiem = match[6].toUpperCase();

  if (meridiem === 'PM' && hours !== 12) {
    hours += 12;
  }
  if (meridiem === 'AM' && hours === 12) {
    hours = 0;
  }

  return new Date(year, month, day, hours, minutes).getTime();
};

const sortMessagesByLatest = (messages) => (
  [...messages].sort((a, b) => parseMessageTimestamp(b.timestamp) - parseMessageTimestamp(a.timestamp))
);

const areMessagesEqual = (leftMessages, rightMessages) => {
  if (leftMessages.length !== rightMessages.length) {
    return false;
  }

  return leftMessages.every((leftMessage, index) => {
    const rightMessage = rightMessages[index];

    return leftMessage.id === rightMessage.id
      && leftMessage.title === rightMessage.title
      && leftMessage.timestamp === rightMessage.timestamp
      && leftMessage.text === rightMessage.text
      && leftMessage.expanded === rightMessage.expanded
      && leftMessage.read === rightMessage.read
      && leftMessage.hiddenFrom === rightMessage.hiddenFrom;
  });
};

const formatNotificationTimestamp = (timestamp) => {
  if (!timestamp) {
    return '';
  }

  const parsedDate = new Date(timestamp);
  if (Number.isNaN(parsedDate.getTime())) {
    return `${timestamp}`;
  }

  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(parsedDate).replace(',', '');
};

const mapNotificationToMessage = (notification, existingMessage) => ({
  id: notification.id,
  title: notification.title || existingMessage?.title || 'Notification',
  timestamp: formatNotificationTimestamp(notification.created_at),
  text: notification.message || existingMessage?.text || '',
  expanded: existingMessage?.expanded ?? false,
  read: existingMessage?.read ?? notification.status !== 'unread',
});

const mapAnnouncementToMessage = (announcement, existingMessage) => ({
  ...announcement,
  expanded: existingMessage?.expanded ?? announcement.expanded,
  read: existingMessage?.read ?? announcement.read,
});

const mergeAnnouncementMessages = (seedMessages, persistedMessages, hiddenMessages) => {
  const normalizedPersistedMessages = Array.isArray(persistedMessages) ? persistedMessages : [];
  const normalizedHiddenMessages = Array.isArray(hiddenMessages) ? hiddenMessages : [];
  const seedIds = new Set(seedMessages.map((message) => String(message.id)));
  const persistedMessagesById = new Map(
    normalizedPersistedMessages.map((message) => [String(message.id), message])
  );
  const hiddenAnnouncementIds = new Set(
    normalizedHiddenMessages
      .filter((message) => message.hiddenFrom === 'announcements')
      .map((message) => String(message.id))
  );

  const mergedSeedMessages = seedMessages
    .filter((message) => !hiddenAnnouncementIds.has(String(message.id)))
    .map((message) => mapAnnouncementToMessage(
      message,
      persistedMessagesById.get(String(message.id))
    ));

  const persistedOnlyMessages = normalizedPersistedMessages.filter(
    (message) => !seedIds.has(String(message.id)) && !hiddenAnnouncementIds.has(String(message.id))
  );

  return sortMessagesByLatest([...mergedSeedMessages, ...persistedOnlyMessages]);
};

const INITIAL_ANNOUNCEMENTS = [
  {
    id: 1,
    title: 'WSD Portal Payment Update',
    timestamp: '04/07/26 11:08 AM',
    text: 'WSD Payments were missing and now have been added to the portal.',
    expanded: true,
    read: false,
  },  
  {
    id: 2,
    title: 'Portal Payment Update',
    timestamp: '04/01/26 01:08 PM',
    text: 'Portal Updated with S8, MRVP, WSD, and HOMEBASE Payments as of 4/01/26',
    expanded: true,
    read: false,
  },
  {
    id: 3,
    title: 'Portal Payment Update',
    timestamp: '03/01/26 05:08 PM',
    text: 'Portal Updated with S8, MRVP, WSD, and HOMEBASE Payments as of 3/01/26',
    expanded: true,
    read: false,
  },
  {
    id: 4,
    title: 'Portal Payment Update',
    timestamp: '02/01/26 10:08 AM',
    text: 'Portal Updated with S8, MRVP, WSD, and HOMEBASE Payments as of 2/01/26',
    expanded: true,
    read: false,
  },
  {
    id: 5,
    title: 'Potential February Payment Delay',
    timestamp: '01/20/26 07:08 AM',
    text: 'We wanted to inform you of a potential delay in rental subsidy payments for the Section 8 and MRVP scheduled to be issued to you in early February. The Commonwealth of Massachusetts is transitioning to a new financial management system, which will require a one-week processing blackout period during which no transactions can be processed. As a result, payments from the Commonwealth of Massachusetts Executive Office of Housing and Livable Communities to Metro Housing and the Regional Administering Agencies (RAAs) may be delayed, and your subsidy payment(s) may be received a few days later than usual. \n EOHLC and the RAAs are prioritizing the timely release of all payments and apologize for any inconvenience this may cause, particularly following the recent voucher software transition. We greatly value your partnership in providing stable housing and appreciate your patience during this period of transition.  We remain fully committed to ensuring accurate and timely payments and to supporting you in the essential role you play in our housing programs.',
    expanded: true,
    read: false,
  },
  {
    id: 6,
    title: 'Missing Remittance Details Update',
    timestamp: '12/15/25 05:08 PM',
    text: 'Missing Remittance Details: \n We are actively working to resolve missing data and provide remittance details. Rest assured, we are handling these cases individually and will continue updating the Portal as additional payment data becomes available from the new system.',
    expanded: true,
    read: false,
  },
  {
    id: 7,
    title: 'Remittance Follow-Up Notice',
    timestamp: '11/15/25 12:08 PM',
    text: 'Missing Remittance Details: \n We are actively working to resolve missing data and provide remittance details. Rest assured, we are handling these cases individually and will continue updating the Portal as additional payment data becomes available from the new system.',
    expanded: false,
    read: false,
  },
  {
    id: 8,
    title: 'Portal Version 2.1 Release',
    timestamp: '08/01/25 12:08 PM',
    text: 'Portal Update Completed: \nThe portal has been updated to version 2.1. New features include enhanced security measures and a more user-friendly interface.',
    expanded: false,
    read: false,
  },
  {
    id: 9,
    title: 'System Outage Notice',
    timestamp: '01/26/25 11:57 AM',
    text: 'System Outage Notice: \nWe are currently experiencing a system outage. Our team is working to resolve the issue as quickly as possible.',
    expanded: false,
    read: false,
  },
];

const MESSAGES_STATE_STORAGE_KEY = 'portal_messages_state_v1';

const loadPersistedMessagesState = () => {
  try {
    const raw = localStorage.getItem(MESSAGES_STATE_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (error) {
    return null;
  }
};

const savePersistedMessagesState = (state) => {
  try {
    localStorage.setItem(MESSAGES_STATE_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    // Ignore storage write failures (private mode/quota)
  }
};

const getMessageTitle = (msg) => {
  if (msg?.title && `${msg.title}`.trim()) {
    return msg.title;
  }

  const firstLine = `${msg?.text || ''}`.split('\n')[0].trim();
  if (firstLine) {
    return firstLine.length > 70 ? `${firstLine.slice(0, 70)}...` : firstLine;
  }

  return 'Untitled Message';
};

export const ProtectedPage = () => {
  const { user } = useAuthenticator((context) => [context.user]);
  const persistedState = loadPersistedMessagesState();
  const { data: notificationResponse } = useGetNotificationsQuery({
    page: 1,
    limit: 100,
    sortBy: 'created_at',
    sortOrder: 'DESC',
  });

  const [selectedMessageSection, setSelectedMessageSection] = useState(
    () => persistedState?.selectedMessageSection || 'announcements'
  );
  const [announcementMessages, setAnnouncementMessages] = useState(() => {
    return mergeAnnouncementMessages(
      INITIAL_ANNOUNCEMENTS,
      persistedState?.announcementMessages,
      persistedState?.hiddenMessages
    );
  });
  const [inboxMessages, setInboxMessages] = useState(() => {
    if (Array.isArray(persistedState?.inboxMessages)) {
      return sortMessagesByLatest(persistedState.inboxMessages);
    }

    return [];
  });
  const [hiddenMessages, setHiddenMessages] = useState(() => {
    if (Array.isArray(persistedState?.hiddenMessages)) {
      return sortMessagesByLatest(persistedState.hiddenMessages);
    }

    return [];
  });

  useEffect(() => {
    setAnnouncementMessages((previousMessages) => {
      const nextMessages = mergeAnnouncementMessages(
        INITIAL_ANNOUNCEMENTS,
        previousMessages,
        hiddenMessages
      );

      return areMessagesEqual(previousMessages, nextMessages)
        ? previousMessages
        : nextMessages;
    });
  }, [hiddenMessages]);

  useEffect(() => {
    const notifications = Array.isArray(notificationResponse?.notifications)
      ? notificationResponse.notifications
      : [];

    if (notifications.length === 0) {
      return;
    }

    const hiddenInboxIds = new Set(
      hiddenMessages
        .filter((message) => message.hiddenFrom === 'inbox')
        .map((message) => String(message.id))
    );

    setInboxMessages((previousMessages) => {
      const previousMessagesById = new Map(
        previousMessages.map((message) => [String(message.id), message])
      );

      const nextMessages = sortMessagesByLatest(
        notifications
          .filter((notification) => !hiddenInboxIds.has(String(notification.id)))
          .map((notification) => mapNotificationToMessage(
            notification,
            previousMessagesById.get(String(notification.id))
          ))
      );

      return areMessagesEqual(previousMessages, nextMessages)
        ? previousMessages
        : nextMessages;
    });

    setHiddenMessages((previousMessages) => {
      const notificationsById = new Map(
        notifications.map((notification) => [String(notification.id), notification])
      );

      const nextMessages = previousMessages.map((message) => {
        if (message.hiddenFrom !== 'inbox') {
          return message;
        }

        const notification = notificationsById.get(String(message.id));
        if (!notification) {
          return message;
        }

        return {
          ...mapNotificationToMessage(notification, message),
          hiddenFrom: 'inbox',
        };
      });

      const sortedMessages = sortMessagesByLatest(nextMessages);
      return areMessagesEqual(previousMessages, sortedMessages)
        ? previousMessages
        : sortedMessages;
    });
  }, [notificationResponse, hiddenMessages]);

  const currentMessages = selectedMessageSection === 'announcements'
    ? announcementMessages
    : selectedMessageSection === 'inbox'
      ? inboxMessages
      : hiddenMessages;

  const sortedCurrentMessages = sortMessagesByLatest(currentMessages);

  const updateMessages = (updater) => {
    if (selectedMessageSection === 'announcements') {
      setAnnouncementMessages(updater);
      return;
    }

    if (selectedMessageSection === 'inbox') {
      setInboxMessages(updater);
      return;
    }

    setHiddenMessages(updater);
  };

  const hideMessage = (id) => {
    if (selectedMessageSection === 'hidden') {
      return;
    }

    const sourceMessages = selectedMessageSection === 'announcements'
      ? announcementMessages
      : inboxMessages;

    const messageToHide = sourceMessages.find((msg) => msg.id === id);
    if (!messageToHide) {
      return;
    }

    if (selectedMessageSection === 'announcements') {
      setAnnouncementMessages(sortMessagesByLatest(sourceMessages.filter((msg) => msg.id !== id)));
    } else {
      setInboxMessages(sortMessagesByLatest(sourceMessages.filter((msg) => msg.id !== id)));
    }

    setHiddenMessages((prev) => sortMessagesByLatest([{ ...messageToHide, hiddenFrom: selectedMessageSection }, ...prev]));
  };

  const showMessage = (id) => {
    if (selectedMessageSection !== 'hidden') {
      return;
    }

    const messageToShow = hiddenMessages.find((msg) => msg.id === id);
    if (!messageToShow) {
      return;
    }

    setHiddenMessages((prev) => sortMessagesByLatest(prev.filter((msg) => msg.id !== id)));

    if (messageToShow.hiddenFrom === 'inbox') {
      setInboxMessages((prev) => sortMessagesByLatest([{ ...messageToShow }, ...prev]));
      return;
    }

    setAnnouncementMessages((prev) => sortMessagesByLatest([{ ...messageToShow }, ...prev]));
  };

  const toggleRead = (id) => {
    updateMessages((messages) => messages.map(msg =>
      msg.id === id ? { ...msg, read: true } : msg
    ));
  };

  const toggleExpand = (id) => {
    updateMessages((messages) => messages.map(msg =>
      msg.id === id ? { ...msg, expanded: !msg.expanded } : msg
    ));
  };

  useEffect(() => {
    savePersistedMessagesState({
      selectedMessageSection,
      announcementMessages,
      inboxMessages,
      hiddenMessages,
    });
  }, [selectedMessageSection, announcementMessages, inboxMessages, hiddenMessages]);

  return (

    <PortalPageLayout user={user}>
      <Authenticator className='login_container container' loginMechanisms={['email']} hideSignUp>

        <Container>
            <Row>
              <Col  xs={12} md={12} lg={12} xl={12}>
                  <MessagesData/>
              </Col>
              <Col  xs={12} md={12} lg={12} xl={12}>
              
              <div className="messages-section-picker">
                <button
                  type="button"
                  className={`message_section_button ${selectedMessageSection === 'announcements' ? 'active' : ''}`}
                  onClick={() => setSelectedMessageSection('announcements')}
                >
                  Announcements ({announcementMessages.length})
                </button>
                <button
                  type="button"
                  className={`message_section_button ${selectedMessageSection === 'inbox' ? 'active' : ''}`}
                  onClick={() => setSelectedMessageSection('inbox')}
                >
                  Inbox ({inboxMessages.length})
                </button>
                <button
                  type="button"
                  className={`message_section_button ${selectedMessageSection === 'hidden' ? 'active' : ''}`}
                  onClick={() => setSelectedMessageSection('hidden')}
                >
                  Hidden ({hiddenMessages.length})
                </button>
              </div>

              <div className="messages-container">
                <h2 className='emphTitle'>
                  {selectedMessageSection === 'announcements'
                    ? 'Announcements'
                    : selectedMessageSection === 'inbox'
                      ? 'Inbox'
                      : 'Hidden'} ({currentMessages.length})
                </h2>
                {currentMessages.length === 0 && (
                  <div className="message empty-message">
                    <p>
                      {selectedMessageSection === 'announcements'
                        ? 'No announcements are available right now.'
                        : selectedMessageSection === 'inbox'
                          ? 'No personal inbox messages are available right now.'
                          : 'No hidden messages right now.'}
                    </p>
                  </div>
                )}
                {sortedCurrentMessages.map(msg => (
                  <div key={msg.id} className={`message ${msg.read ? 'read' : ''}`}>
                    <div className="m_header">
                      <span className="timestamp">{msg.timestamp}</span>
                      <button className='message_button hide' onClick={() => toggleRead(msg.id)}>Mark as Read</button>
                    </div>
                    <h5>{getMessageTitle(msg)}</h5>
                    {selectedMessageSection === 'hidden' ? (
                      <p><em>Message text is hidden.</em></p>
                    ) : selectedMessageSection === 'announcements' ? (
                      msg.expanded ? <p>{msg.text}</p> : null
                    ) : (
                      <p>{msg.expanded ? msg.text : msg.text.split('\n')[0]}</p>
                    )}
                    {selectedMessageSection !== 'hidden' && (
                      <div className='message_actions'>
                        <button className='message_button' onClick={() => toggleExpand(msg.id)}>
                          {msg.expanded ? 'CLOSE MESSAGE' : 'OPEN MESSAGE'}
                        </button>
                        <button className='message_button' onClick={() => hideMessage(msg.id)}>Hide</button>
                      </div>
                    )}
                    {selectedMessageSection === 'hidden' && (
                      <div className='message_actions'>
                        <button className='message_button' onClick={() => showMessage(msg.id)}>Show</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              </Col>

            </Row>

          </Container>
      </Authenticator>
    </PortalPageLayout>
  );
};